import React, { useCallback, useContext } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IBankSettingsContext, BankSettingsContext } from '../../bankSettingsState/bankSettingsContext';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { ServerErrorTypes } from 'types';
import { updateRestaurantBankSettings } from 'backend/restaurants';
import { convertUndefinedFields } from 'utils/helpers';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import classNames from 'classnames';
import style from './ModalWindows.module.scss';

const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { changeTypeOfModalWindow, refetchData, dataForSubmit, typeOfModalWindow } =
		useContext<IBankSettingsContext>(BankSettingsContext);
	const history = useHistory();
	const containerClasses = classNames(style.container, { [style.visible]: typeOfModalWindow });

	const handleRedirect = useCallback(() => {
		history.push('/clients/restaurants');
	}, [history]);

	const handleSubmitWatchesInfo = useCallback(async () => {
		if (dataForSubmit) {
			try {
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await updateRestaurantBankSettings(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				if (axios.isAxiosError(err) && err.response?.data.Type === ServerErrorTypes.BPA_VALIDATION_FAILED) {
					changeTypeOfModalWindow('errorBpaSettings');
				} else {
					changeTypeOfModalWindow('error');
				}
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit]);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, []);

	const handleUpdateData = useCallback(() => {
		refetchData(true);
		changeTypeOfModalWindow(null);
	}, []);

	return (
		<div className={containerClasses} data-test="ModalWindows">
			{typeOfModalWindow === 'toConfirmFieldsClearing' && (
				<ModalToConfirm
					onConsentEvent={handleRedirect}
					onFailureEvent={handleCloseModal}
					isVisible={true}
					description="cancelSaveInfo"
				/>
			)}
			{typeOfModalWindow === 'toConfirmSubmit' && (
				<ModalToConfirm
					onConsentEvent={handleSubmitWatchesInfo}
					onFailureEvent={handleCloseModal}
					isVisible={true}
					description="saveInfo"
				/>
			)}
			{typeOfModalWindow === 'imageFileSizeExceeded' && (
				<ModalSentError
					onConsentEvent={handleCloseModal}
					isVisible={true}
					description="fileSizeExceeded"
					translationOptions={{ size: '1MB' }}
				/>
			)}
			{typeOfModalWindow === 'success' && (
				<ModalSentSuccessfully
					onConsentEvent={handleUpdateData}
					isVisible={true}
					description="savedSuccessfully"
				/>
			)}
			{typeOfModalWindow === 'error' && (
				<ModalSentError onConsentEvent={handleCloseModal} isVisible={true} description="errorWhileSaving" />
			)}
			{typeOfModalWindow === 'errorBpaSettings' && (
				<ModalSentError onConsentEvent={handleCloseModal} isVisible={true} description="errorBpaSettings" />
			)}
		</div>
	);
};

export default ModalWindows;
